import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/@core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/@core/auth/guards/noAuth.guard';
import { TenantGuard } from 'app/@core/auth/guards/tenant.guard';
import { SystemAdminGuard } from 'app/@core/auth/guards/system-admin.guard';
import { SuperAdminGuard } from 'app/@core/auth/guards/super-admin.guard';
import { NativeAppGuard } from 'app/@core/auth/guards/native-app.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/dashboard'
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

    // Redirect signed-in user to the '/dashboard'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/views/sign-in/sign-in.routes'),
            },
            {
                path: 'confirmation',
                loadChildren: () =>
                    import(
                        'app/modules/auth/views/auth-confirmation/auth-confirmation.routes'
                    ),
            },
            {
                path: 'auth/otp',
                loadChildren: () =>
                    import('app/modules/auth/views/auth-otp/auth-otp.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/views/sign-out/sign-out.routes'),
            },
        ],
    },

    // External signing — no auth required, publicly accessible via tokenized link
    {
        path: 'sign/:uuid',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () =>
            import('app/modules/external-signing/external-signing.routes'),
    },

    // Public document verification — no login required (M7-05)
    {
        path: 'verify',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () =>
            import('app/modules/verify/verify-document.routes'),
    },

    // Public User Guide (external-participant flows) — gated by email + OTP,
    // no login required. Own top-level route, never a canActivate guard here
    // (matches sign/:uuid and verify above) — gating lives inside the
    // module's own child routes via UserGuideSessionGuard.
    {
        path: 'guide',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () =>
            import('app/modules/user-guide/public-user-guide.routes'),
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
            // Tenant suspension landing page — no auth guard, accessible to anyone.
            // The FirebasePermissionErrorHandler redirects here on permission-denied.
            {
                path: 'suspended',
                loadChildren: () =>
                    import('app/modules/auth/views/suspended/suspended.routes'),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard, TenantGuard],
        canActivateChild: [AuthGuard, TenantGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('./layout/common/authenticated-wrapper/authenticated-wrapper.component')
                        .then(m => m.AuthenticatedWrapperComponent),
                children: [
                    {
                        path: 'dashboard',
                        canActivate: [NativeAppGuard],
                        loadChildren: () =>
                            import('app/modules/dashboard/dashboard.routes'),
                    },
                    {
                        path: 'admin',
                        canActivate: [NativeAppGuard, SystemAdminGuard],
                        canActivateChild: [SystemAdminGuard],
                        loadChildren: () =>
                            import('app/modules/admin/admin.routes'),
                    },
                    {
                        path: 'requests',
                        loadChildren: ()=>
                            import('app/modules/requests/requests.routes')
                    },
                    {
                        path: 'platform-admin',
                        canActivate: [NativeAppGuard, SuperAdminGuard],
                        canActivateChild: [SuperAdminGuard],
                        loadChildren: () =>
                            import('app/modules/platform-admin/platform-admin.routes'),
                    },
                    {
                        path: 'profile',
                        canActivate: [NativeAppGuard],
                        loadChildren: () =>
                            import('app/modules/profile/profile.routes'),
                    },
                    {
                        path: 'user-guide',
                        canActivate: [NativeAppGuard],
                        loadChildren: () =>
                            import('app/modules/user-guide/user-guide.routes'),
                    },
                ],
            },
        ],
    },
];
